import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroTextButton } from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys } from '../../../shared/hooks';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getResponseData } from '../../../shared/utils/axios';
import { TermSheetFinancialCovenant } from '../../../shared/models';

interface CovenantDeleteDialogProps {
	dealGuid: string | undefined;
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	covenant: TermSheetFinancialCovenant | null;
	close: () => void;
}

export const CovenantDeleteDialog: React.FC<CovenantDeleteDialogProps> = ({
	dealGuid,
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	covenant,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onDeleteConfirmation = () => {
		if (covenant !== null) deleteCovenant(covenant);
	};

	const { mutate: deleteCovenant } = useMutation<void, unknown, TermSheetFinancialCovenant>(
		collateralToDelete =>
			getResponseData(
				api.delete<void>(
					CerebroApiEndpoints.TermSheets.CovenantSpecific(
						dataRoomGuid,
						termSheetGuid,
						revisionGuid,
						collateralToDelete.guid
					)
				)
			),
		{
			onMutate: () => {
				close();
			},
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.TermSheets.Revision(dealGuid, termSheetGuid, revisionGuid));
				snackbar.enqueueSnackbar('Covenant Deleted.', { variant: 'success' });
				close();
			},
			onError: () => {
				snackbar.enqueueSnackbar('There was a problem deleting the covenant.', { variant: 'error' });
			},
		}
	);

	return (
		<Dialog open={covenant !== null} onClose={close}>
			<DialogTitle>Confirm Delete Covenant</DialogTitle>
			<DialogContent>
				<DialogContentText>Are you sure you want to delete {covenant?.covenantName}?</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onDeleteConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
